import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { autoSignIn, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../useAuth';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      // Complete the autoSignIn flow primed during signUp so the user lands
      // authenticated without needing to re-enter credentials.
      try {
        await autoSignIn();
        await refresh();
        navigate('/connect', { replace: true });
        return;
      } catch {
        // autoSignIn isn't available (e.g. user came back after closing tab)
        // — fall through to the manual sign-in path.
        navigate(`/signin?email=${encodeURIComponent(email)}`);
        return;
      }
    } catch (e) {
      setErr(e.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setErr(null);
    setInfo(null);
    try {
      await resendSignUpCode({ username: email });
      setInfo('Code resent. Check your inbox.');
    } catch (e) {
      setErr(e.message || 'Resend failed');
    }
  }

  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6">
      <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4">
        <h1 className="text-3xl font-semibold mb-2">Verify your email</h1>
        <p className="text-sm text-neutral-400 mb-4">
          We sent a 6-digit code to <strong className="text-neutral-200">{email}</strong>.
        </p>
        <input
          type="text"
          required
          inputMode="numeric"
          pattern="\d{6}"
          placeholder="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full px-3 py-2 rounded bg-neutral-900 border border-neutral-800 focus:border-indigo-500 outline-none tracking-widest text-center"
        />
        {err && <p className="text-sm text-red-400">{err}</p>}
        {info && <p className="text-sm text-emerald-400">{info}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full px-4 py-2 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-medium"
        >
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <button
          type="button"
          onClick={resend}
          className="w-full text-sm text-indigo-400 hover:text-indigo-300"
        >
          Resend code
        </button>
      </form>
    </main>
  );
}
